// src/utils/sheetSync/bookingDataMaster.js
//
// Pass 50S — parse the SAS "Booking Data Master" sheet into canonical booking-source records.
// PURE (no I/O, no logging of PII). Maps columns by HEADER NAME (robust to column insertion/
// movement). Only Booking Data Master feeds bookings — never the Instructor 1…20 tabs.

import { opsInstant, opsDateKey } from '../opsTime';
import { mapSamDiscipline, mapSamSkillLevel } from '../scheduleModel';

export const EXTERNAL_SOURCE = 'sas_master_schedule';
export const PILOT_SEASON_ID = '2026_27';
// item 3 — the ONE canonical spreadsheet identity. Production mode ALWAYS validates against this;
// a --spreadsheet argument may only override it in an explicit fixture/dev mode.
export const SAS_MASTER_SPREADSHEET_ID = '1j3yzlj9QiVdvjPoPiC1boWRAfllDf6oZP_MRwMViL1M';

// Header → canonical field. Matched case-insensitively / trimmed / partial where the sheet has
// typos or emoji ("🌐 Accomidation", "Booking Disapline").
const HEADER_MAP = [
  [/^motus.?id/i, 'motusId'],
  [/^instructor$/i, 'instructorLabel'],
  [/invoice/i, 'invoiceNumber'],
  [/roomboss/i, 'roombossNumber'],
  [/^status/i, 'status'],
  [/refferal|referral/i, 'referral'],
  [/lesson sold|price/i, 'price'],
  [/^date$/i, 'date'],
  [/lead guest full name/i, 'leadGuestName'],
  [/booking type/i, 'bookingType'],
  // NB: "extra guests" must be matched BEFORE the generic D.O.B rule, because the real column
  // "Names and D.O.B of Extra Guests" also contains "D.O.B" and would otherwise be mis-claimed.
  [/extra guests/i, 'additionalGuests'],
  [/d\.?o\.?b/i, 'leadGuestDob'],
  [/accomidation.*address|accommodation.*address|accomidation \/|^.*accomidation/i, 'accommodation'],
  [/google pin/i, 'accommodationMapUrl'],
  [/email/i, 'email'],
  [/whatsapp|phone/i, 'phone'],
  [/start time/i, 'startTime'],
  [/disapline|discipline/i, 'discipline'],
  [/skill/i, 'skillLevel'],
  [/number\s*of guests/i, 'groupSize'],
  [/meeting|pick ?up/i, 'pickup'],
  [/^notes/i, 'notes'],
];

// Build { field: colIndex } from the header row.
export function mapHeaders(headerRow = []) {
  const idx = {};
  headerRow.forEach((raw, i) => {
    const h = String(raw || '').trim();
    if (!h) return;
    for (const [re, field] of HEADER_MAP) {
      if (idx[field] === undefined && re.test(h)) { idx[field] = i; break; }
    }
  });
  return idx;
}

// Required headers for a valid Booking Data Master read (deletion-safety precondition).
export const REQUIRED_FIELDS = ['instructorLabel', 'status', 'date', 'bookingType'];
export function validateHeaders(headerRow = []) {
  const idx = mapHeaders(headerRow);
  const missing = REQUIRED_FIELDS.filter((f) => idx[f] === undefined);
  return { ok: missing.length === 0, missing, idx };
}

// ── Stable Motus ID ───────────────────────────────────────────────────────────
// MOTUS-<seasonYY>-<6-digit seq>. Belongs to the row/slot, must survive sort/move/edit. The
// sheet stores it in a hidden "Motus ID" column so identity is independent of row number.
export function seasonYY(seasonId = PILOT_SEASON_ID) { return String(seasonId).slice(2, 4); }
export function makeMotusId(seq, seasonId = PILOT_SEASON_ID) { return `MOTUS-${seasonYY(seasonId)}-${String(seq).padStart(6, '0')}`; }
export const MOTUS_ID_RE = /^MOTUS-\d{2}-\d{6}$/;
export const isMotusId = (v) => MOTUS_ID_RE.test(String(v || '').trim());

// Next sequence number given the Motus IDs already present in the sheet (max+1).
// NOTE: pure helper only. Concurrent-safe allocation is enforced by the lease-bound
// allocator (see sheetSync/motusIdAllocation.js) — never call this directly from a writer.
export function nextMotusSeq(existingIds = []) {
  const nums = existingIds.map((id) => Number(String(id || '').match(/-(\d{6})$/)?.[1])).filter((n) => Number.isFinite(n));
  return (nums.length ? Math.max(...nums) : 0) + 1;
}

// Season prefix ("26") embedded in a Motus ID, or '' if malformed.
export const motusIdSeason = (id) => (MOTUS_ID_RE.test(String(id || '').trim()) ? String(id).trim().slice(6, 8) : '');

// Pass 54 A.1/final — the OPERATIONAL/business rows: the full 2D source with the TECHNICAL Motus ID
// column removed (identified by canonical header, NOT a hardcoded column). Hashing THIS gives the
// operationalSourceRevision, so the technical Motus ID column never changes the revision the
// stale-plan/recovery guard compares (raw revision still changes — keep it for audit).
//
// CRITICAL normalisation (Pass 54 final): Google Sheets values.get omits TRAILING blank cells, so a
// row whose last business cell (e.g. Notes) is blank returns [A..T] before column V existed but
// [A..T, '', MOTUS-…] after. Removing V would leave [A..T, ''] — semantically identical business
// data but a different JSON hash. So after removing the Motus ID column we TRIM TRAILING empty
// ('' / null / undefined) cells (never 0/false; interior blanks keep their position). This applies
// whether or not a Motus ID column exists, so the operational revision is representation-invariant.
const isEmptyCell = (v) => v === '' || v === null || v === undefined; // NOT 0, NOT false
function trimTrailingEmpty(row) {
  let end = row.length;
  while (end > 0 && isEmptyCell(row[end - 1])) end -= 1;
  return row.slice(0, end);
}
export function operationalRows(rows = [], headerRow = []) {
  const idx = mapHeaders(headerRow);
  const col = idx.motusId;
  return rows.map((r) => trimTrailingEmpty(col === undefined ? [...r] : r.filter((_, i) => i !== col)));
}

// item 9/10 — validate EVERY populated Motus ID before a snapshot may be trusted.
// Fails closed: any malformed / wrong-season / duplicate ID makes the whole set invalid so
// the caller performs ZERO creates/updates/cancels. Blank IDs are NOT errors (new rows get one
// allocated). Returns { ok, errors:[{type, motusId, sheetRow(s)}] } with type ∈
// malformed | wrong_season | duplicate.
export function validateMotusIdSet(records = [], { seasonId = PILOT_SEASON_ID } = {}) {
  const yy = seasonYY(seasonId);
  const errors = [];
  const rowsById = new Map();
  for (const r of records) {
    const id = String(r.motusId || '').trim();
    if (!id) continue; // blank → new row, allocated under lease later
    if (!MOTUS_ID_RE.test(id)) { errors.push({ type: 'malformed', motusId: id, sheetRow: r.sheetRow }); continue; }
    if (motusIdSeason(id) !== yy) errors.push({ type: 'wrong_season', motusId: id, sheetRow: r.sheetRow, expected: yy });
    if (!rowsById.has(id)) rowsById.set(id, []);
    rowsById.get(id).push(r.sheetRow);
  }
  for (const [id, rows] of rowsById) {
    if (rows.length > 1) errors.push({ type: 'duplicate', motusId: id, sheetRows: rows });
  }
  return { ok: errors.length === 0, errors };
}

// JST date/time from the sheet. Live Sheets returns strings; a downloaded xlsx returns serials.
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30); // Excel serial 0
function excelSerialToJstDateKey(serial) {
  const ms = EXCEL_EPOCH_MS + Math.round(Number(serial)) * 86400000; // date at UTC midnight
  // that calendar date IS the intended JST date
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
export function toDateKey(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return excelSerialToJstDateKey(v);
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = s.match(/^(\d{1,2})[/](\d{1,2})[/](\d{4})/); // d/m/Y or m/d/Y — assume d/m/Y (Sam's locale)
  if (dmy) return `${dmy[3]}-${String(dmy[2]).padStart(2, '0')}-${String(dmy[1]).padStart(2, '0')}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : opsDateKey(d);
}
// item 4 — parse a clock time to 24h HH:MM. Handles Excel/Sheets numeric serials, plain 24h
// strings (09:00 / 13:00), AND 12h AM/PM strings (9:00 AM → 09:00, 1:00 PM → 13:00,
// 12:00 AM → 00:00, 12:00 PM → 12:00). Returns '' when unparseable.
export function toTimeHHMM(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number') { const mins = Math.round(v * 24 * 60); return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`; }
  const m = String(v).trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp])\.?\s*[Mm]?\.?$/) || String(v).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return '';
  let h = Number(m[1]);
  const min = m[2];
  const mer = m[3] ? m[3].toLowerCase() : '';
  if (mer === 'a') { if (h === 12) h = 0; }            // 12 AM → 00
  else if (mer === 'p') { if (h !== 12) h += 12; }     // 1 PM → 13, 12 PM stays 12
  if (h > 23 || Number(min) > 59) return '';
  return `${String(h).padStart(2, '0')}:${min}`;
}

// ── Parse ───────────────────────────────────────────────────────────────────
// rows = 2D array (sheet_to_json header:1). headerRowIndex auto-detected if not given.
// Returns { records, headerRowIndex, idx } — records are structured (contain PII fields but this
// function never logs them).
export function parseBookingDataMaster(rows = [], { headerRowIndex } = {}) {
  let hIdx = headerRowIndex;
  if (hIdx == null) {
    let best = 0; hIdx = 0;
    for (let i = 0; i < Math.min(6, rows.length); i++) {
      const n = (rows[i] || []).filter((c) => typeof c === 'string' && c.trim() && c.length <= 40).length;
      if (n > best) { best = n; hIdx = i; }
    }
  }
  const idx = mapHeaders(rows[hIdx] || []);
  const get = (row, field) => (idx[field] === undefined ? '' : row[idx[field]]);
  const records = [];
  for (let r = hIdx + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    if (!row.some((c) => c !== '' && c != null)) continue;               // blank row
    const status = String(get(row, 'status') || '').trim();
    const date = toDateKey(get(row, 'date'));
    const bookingType = String(get(row, 'bookingType') || '').trim();
    if (!status && !date && !bookingType) continue;                       // not a booking row
    const startTime = toTimeHHMM(get(row, 'startTime'));
    records.push({
      motusId: String(get(row, 'motusId') || '').trim(),
      sheetRow: r + 1,                                                     // 1-based, for review only (NOT identity)
      instructorLabel: String(get(row, 'instructorLabel') || '').trim(),
      status,
      date, startTime,
      startAt: date && startTime ? opsInstant(date, startTime) : null,
      bookingType, serviceType: bookingType,
      // Canonical vocabularies ONLY — reuse scheduleModel mappers (no sheet-specific mapping).
      discipline: mapSamDiscipline(get(row, 'discipline')),
      skillLevel: mapSamSkillLevel(get(row, 'skillLevel')),
      groupSize: numOrNull(get(row, 'groupSize')),
      pickup: String(get(row, 'pickup') || '').trim(),
      notes: String(get(row, 'notes') || '').trim(),
      invoiceNumber: String(get(row, 'invoiceNumber') || '').trim(),
      roombossNumber: String(get(row, 'roombossNumber') || '').trim(),
      referral: String(get(row, 'referral') || '').trim(),
      price: numOrNull(get(row, 'price')),
      accommodation: String(get(row, 'accommodation') || '').trim(),
      accommodationMapUrl: String(get(row, 'accommodationMapUrl') || '').trim(),
      // PII (kept for the write payload; callers must NOT log these):
      leadGuestName: String(get(row, 'leadGuestName') || '').trim(),
      email: String(get(row, 'email') || '').trim(),
      phone: String(get(row, 'phone') || '').trim(),
      additionalGuests: String(get(row, 'additionalGuests') || '').trim(),
    });
  }
  return { records, headerRowIndex: hIdx, idx };
}

function numOrNull(v) { const n = Number(v); return Number.isFinite(n) && String(v).trim() !== '' ? n : null; }

// Distinct instructor labels — the PII-free review list for activation.
export function distinctInstructorLabels(records = []) {
  return [...new Set(records.map((r) => r.instructorLabel).filter(Boolean))].sort();
}

export { opsDateKey };
